import { useState } from 'react'
import { useRequest } from 'ahooks'

import { useConfirmModal } from '$contexts/Modal'
import useSignal from '$actions/useSignal'
import useMFABlock from '$actions/useMFABlock'
import use2FAHelper from '$actions/use2FAHelper'
import useCurrentAuthRTC from '$actions/useCurrentAuthRTC'

import { asyncSuccessResolver, toast } from '$app/utilities'
import { ToastMessage } from '$app/types'

import api from '$model/api'
import { errorMessageResolver, successResolver } from '$services/api/common'

export type UseRemoveOptions = {
  onRemoved?: () => void
}

export type UseRemoveReturn = ReturnType<typeof useRemove>

const removeReqToastMsg: ToastMessage = {
  loading: 'Removing..',
  error: errorMessageResolver,
  success: 'Passkey successfully removed.'
}

export const useRemove = ({ onRemoved }: UseRemoveOptions) => {
  const [id, setId] = useState<number>()

  const modal = useConfirmModal({
    onConfirm: async close => {
      if (!id) return

      await toast
        .promise(removeReq.runAsync({ id, mfa_token }), removeReqToastMsg)
        .unwrap()

      close()
    },
    onClose: () => {
      reset()
      mfaBlock.exit()
    },
    title: 'Confirm to Remove Passkey'
  })

  const { mfaAuth: apiInstance } = useSignal(api)
  const authRTC = useCurrentAuthRTC()
  const { mfaRemovePasskey } = apiInstance

  const removeReq = useRequest(
    asyncSuccessResolver(mfaRemovePasskey, successResolver),
    {
      manual: true,
      onError: err => {
        if (twoFAHelper.is2FAError(err)) {
          mfaBlock.modalControls.open()
        }
      },
      onSuccess: () => {
        modal.modalControls.close()
        onRemoved?.()
      }
    }
  )

  const isLoading = removeReq.loading

  const twoFAHelper = use2FAHelper({ getMfaBlock: () => mfaBlock })
  const preferred_factor = twoFAHelper.getPreferredFactorByError(
    removeReq.error
  )
  const available_factors = twoFAHelper.getFactorsByError(removeReq.error)

  const mfaBlock = useMFABlock({
    apiInstance,
    authRTC,
    data: { available_factors, preferred_factor },
    onCancelled: modal.modalControls.close,
    onVerified: mfa_token => {
      const { params, runAsync } = removeReq

      // Make sure there was a previous call
      if (!params.length) return

      const [data] = params

      // Auto retry the request with token
      toast
        .promise(runAsync({ ...data, mfa_token }), removeReqToastMsg)
        .unwrap()
        .catch(twoFAHelper.reChallengeOrExitOnError)
    }
  })

  const { mfa_token } = mfaBlock

  const reset = () => {
    removeReq.cancel()
    removeReq.mutate()
  }

  const onRemove = (id: number) => {
    setId(id)
    modal.modalControls.open()
  }

  return {
    modal,
    onRemove,
    isLoading,
    mfaBlock
  }
}

export default useRemove
